package floris.graphics.repo

import floris.graphics.NetworkingConfig
import floris.graphics.QueryOptions
import floris.graphics.deleteRequest
import floris.graphics.extensions.buildFormData
import floris.graphics.getRequest
import floris.graphics.models.NetworkResponse
import floris.graphics.models.domain.models.Contributor
import floris.graphics.models.domain.models.toContributor
import floris.graphics.models.dto.requests.contributor.ContributorDto
import floris.graphics.models.dto.requests.contributor.InsertOrUpdateContributor
import floris.graphics.models.dto.responses.ErrorResponse
import floris.graphics.postRequest
import floris.graphics.putRequest
import org.w3c.files.File

object ContributorRepo {
    private val contributorsUrl = "${NetworkingConfig.baseUrl}/api/v1/contributors"

    suspend fun getContributors(queryOptions: QueryOptions = QueryOptions()): List<Contributor> {
        val url = contributorsUrl

        val response: NetworkResponse<List<ContributorDto>, ErrorResponse> = getRequest(url, queryOptions)

        return when (response) {
            is NetworkResponse.Success -> response.body.map { it.toContributor() }
            else -> emptyList()
        }
    }

    suspend fun getContributor(contributorId: String): Contributor? {
        val url = "$contributorsUrl/$contributorId"

        val response: NetworkResponse<ContributorDto, ErrorResponse> = getRequest(url)

        return when (response) {
            is NetworkResponse.Success -> response.body.toContributor()
            else -> null
        }
    }

    @Suppress("TooGenericExceptionThrown")
    suspend fun insertContributor(contributor: InsertOrUpdateContributor, logo: File?): Contributor? {
        val url = contributorsUrl

        val formData = buildFormData(
            data = contributor,
            extraFields = mapOf("image" to logo).filterValues { it != null }
        )

        val response: NetworkResponse<ContributorDto?, ErrorResponse> = postRequest(
            resource = url,
            body = formData
        )

        return when (response) {
            is NetworkResponse.Success -> response.body?.toContributor()
            is NetworkResponse.ServerError -> throw RuntimeException(response.body?.errorDescription)
            is NetworkResponse.Error -> throw response.error ?: Exception("Unknown error")
        }
    }

    @Suppress("TooGenericExceptionThrown")
    suspend fun updateContributor(contributorId: String, contributor: InsertOrUpdateContributor): Contributor? {
        val url = "$contributorsUrl/$contributorId"
        val response: NetworkResponse<ContributorDto, ErrorResponse> = putRequest(resource = url, body = contributor)

        return when (response) {
            is NetworkResponse.Success -> response.body.toContributor()
            else -> null
        }
    }

    suspend fun updateContributorImage(contributorId: String, logo: File?, removeLogo: Boolean = false): Contributor? {
        val url = "$contributorsUrl/$contributorId/upload"

        val formData = buildFormData(
            data = mapOf("remove_logo" to removeLogo),
            extraFields = mapOf("image" to logo).filterValues { it != null }
        )

        val response: NetworkResponse<ContributorDto?, ErrorResponse> = putRequest(
            resource = url,
            body = formData
        )

        return when (response) {
            is NetworkResponse.Success -> response.body?.toContributor()
            else -> null
        }
    }

    suspend fun deleteContributor(contributorId: String): Boolean {
        val url = "$contributorsUrl/$contributorId"

        val response = deleteRequest(url)

        return when (response) {
            is NetworkResponse.Success -> true
            else -> false
        }
    }

//    suspend fun deleteContributors(contributorIds: List<String>): Boolean {
//        val url = "$contributorsUrl/batch
//
//        val response: NetworkResponse<Unit, ErrorResponse> = postRequest(url.toString(), body = contributorIds)
//
//        return when (response) {
//            is NetworkResponse.Success -> true
//            else -> false
//        }
//    }
}
